'use client';

import { c as _c } from "react/compiler-runtime";
import { useAccess } from '@repo/auth';
import { type ApiErrorEntry, ApiErrors } from '@repo/features/error';
import { useAppForm } from '@repo/form';
import { permissions } from '@repo/permissions';
import type { DuplicateContainer } from '@repo/schema/page-data';
import { TEST_IDS } from '@repo/sdk/constants';
import { ErrorBoundary } from '@repo/sdk/error';
import { formatCurrency } from '@repo/sdk/helpers';
import { Alert, AlertDescription, AlertTitle, Badge, Button, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, Dialog, DialogContent, DialogHeader, DialogTitle, Field, FieldLabel, Input, Skeleton, Spinner, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { AppWindow, ChevronDown, ChevronRight, CopyPlus, Info, Minus, PanelRight, Plus } from '@repo/ui/icons';
import Link from 'next/link';
import { type ReactNode, Suspense, use, useId, useRef, useState } from 'react';
import { useDuplicateSave } from '@/app/@protected/loads/_hooks/use-duplicate-save.ts';
import { buildDuplicateBody, type DuplicateAccess } from '@/app/@protected/loads/_utils/duplicate-body.ts';
import { type DuplicateCardValues, type DuplicateDateRules, type DuplicateOrigin, type DuplicateStart, getDuplicateCardDefaults, getDuplicateCardErrors, getNextDuplicateCard, withStartDates, withStartRate } from '@/app/@protected/loads/_utils/duplicate-card.ts';
import { type DuplicateSaveOutcome, getCreatedLoadIds, IDLE, isSettled } from '@/app/@protected/loads/_utils/duplicate-save.ts';
import { DuplicateCard, DuplicateCardRate, DuplicateCardSummary } from './duplicate-card.tsx';
const UNCONFIRMED_MESSAGE = 'We couldn’t confirm this load was created. Check the loads list before trying again.';

/**
 * The one Duplicate moving to the other container: its values, its save, and whether it keeps the
 * Original Load's dates and rate. The session's origin moves with the session.
 */
export type CarriedDuplicate = Pick<DuplicateStart, 'keepDates' | 'useOriginalRate'> & {
  card: DuplicateCardValues;
  outcome: DuplicateSaveOutcome;
};
type DuplicateFormValues = {
  duplicates: DuplicateCardValues[];
};
type DuplicateSaving = ReturnType<typeof useDuplicateSave>;

// The flow's content: a form when it can save, otherwise the details it couldn't load.
const FrameContent = t0 => {
  const $ = _c(4);
  const {
    children,
    className,
    onSubmit
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className || $[2] !== onSubmit) {
    t1 = onSubmit ? <form className={className} onSubmit={event => {
      event.preventDefault();
      onSubmit();
    }}>{children}</form> : <div className={className}>{children}</div>;
    $[0] = children;
    $[1] = className;
    $[2] = onSubmit;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};
const TITLE = 'Duplicate load';
// The page title, as the dialog and the side panel both show it.
const TITLE_CLASS_NAME = 'flex items-center gap-3 text-3xl leading-9 font-extrabold';
const titleContent = <>
    <CopyPlus className="size-6" />
    {TITLE}
  </>;

/** The Original Load a Duplicate starts from, with its Route from the first Stop's place to the last's. */
const OriginalLoadNotice = t0 => {
  const $ = _c(8);
  const {
    origin: t1
  } = t0;
  const {
    originalStops,
    template
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Info data-sentry-element="Info" data-sentry-source-file="duplicate-flow.tsx" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const t3 = originalStops.at(0)?.place;
  const t4 = originalStops.at(-1)?.place;
  let t5;
  if ($[1] !== t3 || $[2] !== t4 || $[3] !== template.source_load_id) {
    t5 = <AlertTitle data-sentry-element="AlertTitle" data-sentry-source-file="duplicate-flow.tsx">Create a copy of load {template.source_load_id}: {t3} →{" "}{t4}</AlertTitle>;
    $[1] = t3;
    $[2] = t4;
    $[3] = template.source_load_id;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <AlertDescription data-sentry-element="AlertDescription" data-sentry-source-file="duplicate-flow.tsx">You can edit all the load details after duplication.</AlertDescription>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t5) {
    t7 = <Alert variant="info" data-sentry-element="Alert" data-sentry-component="OriginalLoadNotice" data-sentry-source-file="duplicate-flow.tsx">{t2}{t5}{t6}</Alert>;
    $[6] = t5;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
};
type DuplicateFrameProps = {
  /** The header's button, beside the title. */
  action?: ReactNode;
  body: ReactNode;
  container: DuplicateContainer;
  /**
   * The container's own controls: the dialog's toolbar over its cards, or the side panel's
   * "Create another duplicate" after its body.
   */
  controls?: ReactNode;
  /** The footer's buttons: Cancel first, then the save. */
  footer?: ReactNode;
  /** What the Duplicate starts from, under the title, once it is known. */
  notice?: ReactNode;
  /** Makes the content a form. */
  onSubmit?: () => void;
};

// Each container's chrome around the flow's content: the title, notice, controls and footer.
// Cancel closes either; the side panel has no close button of its own.
const DuplicateFrame = t0 => {
  const $ = _c(34);
  const {
    action,
    body,
    container,
    controls,
    footer,
    notice,
    onSubmit
  } = t0;
  if (container === "dialog") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DialogTitle className={TITLE_CLASS_NAME}>{titleContent}</DialogTitle>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] !== action) {
      t2 = <DialogHeader className="flex-row items-center justify-between gap-2">{t1}{action}</DialogHeader>;
      $[1] = action;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    let t3;
    if ($[3] !== body) {
      t3 = <div className="-mx-6 flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-6">{body}</div>;
      $[3] = body;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    let t4;
    if ($[5] !== footer) {
      t4 = footer ? <div className="flex justify-between gap-2">{footer}</div> : null;
      $[5] = footer;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== controls || $[8] !== notice || $[9] !== onSubmit || $[10] !== t3 || $[11] !== t4) {
      t5 = <FrameContent className="flex min-h-0 flex-1 flex-col gap-4" onSubmit={onSubmit}>{notice}{controls}{t3}{t4}</FrameContent>;
      $[7] = controls;
      $[8] = notice;
      $[9] = onSubmit;
      $[10] = t3;
      $[11] = t4;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== t2 || $[14] !== t5) {
      t6 = <>{t2}{t5}</>;
      $[13] = t2;
      $[14] = t5;
      $[15] = t6;
    } else {
      t6 = $[15];
    }
    return t6;
  }
  let t1;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h2 className={TITLE_CLASS_NAME}>{titleContent}</h2>;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  let t2;
  if ($[17] !== action) {
    t2 = <header className="flex shrink-0 items-center justify-between gap-2 px-6 pt-6 pb-4">{t1}{action}</header>;
    $[17] = action;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== body || $[20] !== controls) {
    t3 = <div className="flex flex-col gap-3 pt-1">{body}{controls}</div>;
    $[19] = body;
    $[20] = controls;
    $[21] = t3;
  } else {
    t3 = $[21];
  }
  let t4;
  if ($[22] !== notice || $[23] !== t3) {
    t4 = <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-6 pb-4">{notice}{t3}</div>;
    $[22] = notice;
    $[23] = t3;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== footer) {
    t5 = footer ? <footer className="flex shrink-0 justify-between gap-2 px-6 pb-6">{footer}</footer> : null;
    $[25] = footer;
    $[26] = t5;
  } else {
    t5 = $[26];
  }
  let t6;
  if ($[27] !== onSubmit || $[28] !== t4 || $[29] !== t5) {
    t6 = <FrameContent className="flex min-h-0 flex-1 flex-col" onSubmit={onSubmit} data-sentry-element="FrameContent" data-sentry-source-file="duplicate-flow.tsx">{t4}{t5}</FrameContent>;
    $[27] = onSubmit;
    $[28] = t4;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  let t7;
  if ($[31] !== t2 || $[32] !== t6) {
    t7 = <section aria-label={TITLE} className="flex h-full min-h-0 w-full min-w-0 flex-col bg-background" data-shortcut-scope={true} data-sentry-component="DuplicateFrame" data-sentry-source-file="duplicate-flow.tsx">{t2}{t6}</section>;
    $[31] = t2;
    $[32] = t6;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  return t7;
};

// The dialog around the flow, kept mounted while its content loads. The side panel's container is
// the app's right panel.
const DuplicateShell = t0 => {
  const $ = _c(4);
  const {
    children,
    container,
    onClose
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== container || $[2] !== onClose) {
    t1 = container === "dialog" ? <Dialog disablePointerDismissal={true} onOpenChange={open => open ? undefined : onClose()} open={true}><DialogContent className="top-[5dvh] flex max-h-[90dvh] translate-y-0 flex-col p-6 sm:max-w-[867px]" data-shortcut-scope={true} data-testid={TEST_IDS.load.duplicateDialog} showCloseButton={false}>{children}</DialogContent></Dialog> : children;
    $[0] = children;
    $[1] = container;
    $[2] = onClose;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
};

// Moves the Duplicate to the other container. Only one Duplicate moves, so it stays focusable
// while the dialog holds more, to explain why on hover or focus.
const SwitchContainerButton = t0 => {
  const $ = _c(14);
  const {
    container,
    disabled,
    holdsSeveralCards,
    onSwitch
  } = t0;
  const label = container === "dialog" ? "Open as side panel" : "Open as dialog";
  const t1 = disabled || holdsSeveralCards;
  let t2;
  if ($[0] !== label || $[1] !== onSwitch || $[2] !== t1) {
    t2 = <Button aria-label={label} className="aria-disabled:opacity-50" disabled={t1} focusableWhenDisabled={true} onClick={onSwitch} size="icon-sm" type="button" variant="ghost" />;
    $[0] = label;
    $[1] = onSwitch;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== container) {
    t3 = container === "dialog" ? <PanelRight /> : <AppWindow />;
    $[4] = container;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <TooltipTrigger render={t2} data-sentry-element="TooltipTrigger" data-sentry-source-file="duplicate-flow.tsx">{t3}</TooltipTrigger>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = holdsSeveralCards ? "The side panel creates one duplicate at a time." : label;
  let t6;
  if ($[9] !== t5) {
    t6 = <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="duplicate-flow.tsx">{t5}</TooltipContent>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <Tooltip data-sentry-element="Tooltip" data-sentry-component="SwitchContainerButton" data-sentry-source-file="duplicate-flow.tsx">{t4}{t6}</Tooltip>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  return t7;
};

// A Duplicate's own feedback: the API's refusal, or a save nobody can confirm.
const getFeedback = (outcome: DuplicateSaveOutcome): ApiErrorEntry[] | null => {
  if (outcome.status === 'unconfirmed') return [UNCONFIRMED_MESSAGE];
  if (outcome.status === 'failed') return outcome.errors;
  return null;
};

// Where a Duplicate's save stands, beside its name.
const SaveStatus = t0 => {
  const $ = _c(9);
  const {
    outcome
  } = t0;
  switch (outcome.status) {
    case "created":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Badge variant="secondary">Created</Badge>;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        const t2 = `/loads/${outcome.loadId}/overview`;
        let t3;
        if ($[1] !== outcome.loadId || $[2] !== t2) {
          t3 = <>{t1}<Link className="text-sm underline underline-offset-2" href={t2}>Load {outcome.loadId}</Link></>;
          $[1] = outcome.loadId;
          $[2] = t2;
          $[3] = t3;
        } else {
          t3 = $[3];
        }
        return t3;
      }
    case "failed":
      {
        let t1;
        if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Badge variant="destructive">Not created</Badge>;
          $[4] = t1;
        } else {
          t1 = $[4];
        }
        return t1;
      }
    case "idle":
    case "queued":
      {
        return null;
      }
    case "sending":
      {
        let t1;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Spinner />;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    case "skipped":
      {
        let t1;
        if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Badge variant="outline">Skipped</Badge>;
          $[6] = t1;
        } else {
          t1 = $[6];
        }
        return t1;
      }
    case "unconfirmed":
      {
        let t1;
        if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Badge variant="destructive">Unconfirmed</Badge>;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
    case "unsent":
      {
        let t1;
        if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <Badge variant="outline">Not sent</Badge>;
          $[8] = t1;
        } else {
          t1 = $[8];
        }
        return t1;
      }
  }
};

// A checkbox with its label beside it.
const CheckboxOption = t0 => {
  const $ = _c(7);
  const {
    checked,
    children,
    disabled,
    onChange
  } = t0;
  let t1;
  if ($[0] !== checked || $[1] !== disabled || $[2] !== onChange) {
    t1 = <Checkbox checked={checked} disabled={disabled} onCheckedChange={onChange} data-sentry-element="Checkbox" data-sentry-source-file="duplicate-flow.tsx" />;
    $[0] = checked;
    $[1] = disabled;
    $[2] = onChange;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== children || $[5] !== t1) {
    t2 = <label className="flex w-fit items-center gap-2 text-sm leading-none font-medium" data-sentry-component="CheckboxOption" data-sentry-source-file="duplicate-flow.tsx">{t1}{children}</label>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

// "Copies": typed, or stepped with the buttons either side. A typed number below `min` waits until
// it is corrected; leaving the field shows the count again.
const DuplicateCount = t0 => {
  const $ = _c(32);
  const {
    count,
    disabled,
    min,
    onChange
  } = t0;
  const id = useId();
  const [typed, setTyped] = useState(null);
  let t1;
  if ($[0] !== id) {
    t1 = <FieldLabel htmlFor={id} data-sentry-element="FieldLabel" data-sentry-source-file="duplicate-flow.tsx">Copies</FieldLabel>;
    $[0] = id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = disabled || count <= min;
  let t3;
  if ($[2] !== count || $[3] !== onChange) {
    t3 = () => onChange(count - 1);
    $[2] = count;
    $[3] = onChange;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Minus data-sentry-element="Minus" data-sentry-source-file="duplicate-flow.tsx" />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3) {
    t5 = <Button aria-label="Fewer copies" disabled={t2} onClick={t3} size="icon-sm" type="button" variant="outline" data-sentry-element="Button" data-sentry-source-file="duplicate-flow.tsx">{t4}</Button>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => setTyped(null);
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== min || $[11] !== onChange) {
    t7 = event => {
      setTyped(event.target.value);
      const next = Number(event.target.value);
      if (Number.isSafeInteger(next) && next >= min) {
        onChange(next);
      }
    };
    $[10] = min;
    $[11] = onChange;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = typed ?? count;
  let t9;
  if ($[13] !== disabled || $[14] !== id || $[15] !== t7 || $[16] !== t8) {
    t9 = <Input className="h-7 w-10 px-1 text-center tabular-nums" disabled={disabled} id={id} inputMode="numeric" onBlur={t6} onChange={t7} value={t8} data-sentry-element="Input" data-sentry-source-file="duplicate-flow.tsx" />;
    $[13] = disabled;
    $[14] = id;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== count || $[19] !== onChange) {
    t10 = () => onChange(count + 1);
    $[18] = count;
    $[19] = onChange;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Plus data-sentry-element="Plus" data-sentry-source-file="duplicate-flow.tsx" />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== disabled || $[23] !== t10) {
    t12 = <Button aria-label="More copies" disabled={disabled} onClick={t10} size="icon-sm" type="button" variant="outline" data-sentry-element="Button" data-sentry-source-file="duplicate-flow.tsx">{t11}</Button>;
    $[22] = disabled;
    $[23] = t10;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t5 || $[27] !== t9) {
    t13 = <div className="flex items-center gap-1">{t5}{t9}{t12}</div>;
    $[25] = t12;
    $[26] = t5;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t1 || $[30] !== t13) {
    t14 = <Field className="w-fit items-center gap-3" orientation="horizontal" data-sentry-element="Field" data-sentry-component="DuplicateCount" data-sentry-source-file="duplicate-flow.tsx">{t1}{t13}</Field>;
    $[29] = t1;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  return t14;
};

// One Duplicate's card: its name ("Copy", or "Copy N" among several), save status and, while open,
// its rate over its fields, which fold away to a summary line. Folding slides the fields on their
// own height into a slight blur; a card added to the count settles in and flashes its border once,
// so the eye finds it.
const DuplicateSection = t0 => {
  const $ = _c(25);
  const {
    card,
    label,
    onOpenChange,
    open,
    outcome,
    rate,
    summary
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <section aria-label={label} />;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== open) {
    t2 = open ? <ChevronDown className="size-5" /> : <ChevronRight className="size-5" />;
    $[2] = open;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== label || $[5] !== t2) {
    t3 = <CollapsibleTrigger className="flex items-center gap-2 text-lg leading-none font-bold" data-sentry-element="CollapsibleTrigger" data-sentry-source-file="duplicate-flow.tsx">{label}{t2}</CollapsibleTrigger>;
    $[4] = label;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== outcome) {
    t4 = <SaveStatus outcome={outcome} data-sentry-element="SaveStatus" data-sentry-source-file="duplicate-flow.tsx" />;
    $[7] = outcome;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== open || $[10] !== rate || $[11] !== summary) {
    t5 = open ? <div className="ml-auto">{rate}</div> : summary;
    $[9] = open;
    $[10] = rate;
    $[11] = summary;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t3 || $[14] !== t4 || $[15] !== t5) {
    t6 = <div className="flex min-h-8 flex-wrap items-center gap-2">{t3}{t4}{t5}</div>;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== card) {
    t7 = <CollapsibleContent className="h-(--collapsible-panel-height) overflow-hidden pt-6 transition-[height,opacity,filter] duration-220 ease-[cubic-bezier(0.16,1,0.3,1)] data-ending-style:h-0 data-ending-style:opacity-0 data-ending-style:blur-[2px] data-starting-style:h-0 data-starting-style:opacity-0 data-starting-style:blur-[2px] motion-reduce:transition-opacity motion-reduce:data-ending-style:blur-none motion-reduce:data-starting-style:blur-none" keepMounted={true} data-sentry-element="CollapsibleContent" data-sentry-source-file="duplicate-flow.tsx">{card}</CollapsibleContent>;
    $[17] = card;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== onOpenChange || $[20] !== open || $[21] !== t1 || $[22] !== t6 || $[23] !== t7) {
    t8 = <Collapsible className="origin-top rounded-lg border px-3 py-3 not-first:animate-[duplicate-card-settle_240ms_cubic-bezier(0.16,1,0.3,1),duplicate-card-flash_900ms_ease-out]" onOpenChange={onOpenChange} open={open} render={t1} data-sentry-element="Collapsible" data-sentry-component="DuplicateSection" data-sentry-source-file="duplicate-flow.tsx">{t6}{t7}</Collapsible>;
    $[19] = onOpenChange;
    $[20] = open;
    $[21] = t1;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
};

// The `index`th card's path in the form; its fields' paths start with it.
const cardPath = (index: number) => `duplicates[${index}]` as const;
const useDuplicateAccess = () => {
  const $ = _c(9);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      permissions: [permissions.loads.assigneesManage]
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = useAccess(t0);
  const t2 = t1.allowed;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      permissions: [permissions.loads.customsManage]
    };
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = useAccess(t3);
  const t5 = t4.allowed;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      permissions: [permissions.loads.estimatedCostsManage]
    };
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  const t7 = useAccess(t6);
  const t8 = t7.allowed;
  let t9;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      permissions: [permissions.loads.updateDropPastDates]
    };
    $[3] = t9;
  } else {
    t9 = $[3];
  }
  const t10 = useAccess(t9);
  let t11;
  if ($[4] !== t1.allowed || $[5] !== t10.allowed || $[6] !== t4.allowed || $[7] !== t7.allowed) {
    t11 = {
      canManageAssignees: t2,
      canManageCustoms: t5,
      canManageEstimatedCosts: t8,
      canSetPastDates: t10.allowed
    };
    $[4] = t1.allowed;
    $[5] = t10.allowed;
    $[6] = t4.allowed;
    $[7] = t7.allowed;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  return t11;
};

// `settled`: the cards created or skipped, which are never sent again.
const getSaveLabel = (count: number, settled: number) => {
  if (count === 1) return 'Duplicate load';
  const remaining = count - settled;
  const loads = remaining === 1 ? 'load' : 'loads';
  return settled ? `Duplicate ${remaining} remaining ${loads}` : `Duplicate ${remaining} ${loads}`;
};
const DuplicateForm = t0 => {
  const $ = _c(100);
  const {
    carried,
    container,
    count,
    onClose,
    onCountChange,
    onCreated,
    onSaved,
    onSwitch,
    origin,
    saving: t1
  } = t0;
  const {
    outcomes,
    reset: resetOutcome,
    save,
    sending,
    skip
  } = t1;
  const opened = use(origin);
  const {
    originalStops,
    template
  } = opened;
  const access = useDuplicateAccess();
  const [today] = useState(_temp);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [collapsed, setCollapsed] = useState(t2);
  const [createAnother, setCreateAnother] = useState(false);
  const [keepDates, setKeepDates] = useState(carried?.keepDates ?? false);
  const [useOriginalRate, setUseOriginalRate] = useState(carried?.useOriginalRate ?? false);
  const start = {
    keepDates,
    originalStops,
    template,
    useOriginalRate
  };
  const checkingRef = useRef(false);
  let t3;
  if ($[1] !== access.canSetPastDates) {
    t3 = now => ({
      canSetPastDates: access.canSetPastDates,
      today: now
    });
    $[1] = access.canSetPastDates;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const dateRules = t3;
  let t4;
  if ($[3] !== outcomes) {
    t4 = index => isSettled(outcomes[index]);
    $[3] = outcomes;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const isLocked = t4;
  let t5;
  if ($[5] !== outcomes) {
    t5 = outcomes.filter(isSettled);
    $[5] = outcomes;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const settledCount = t5.length;
  let t6;
  if ($[7] !== dateRules || $[8] !== isLocked) {
    t6 = t7 => {
      const {
        duplicates
      } = t7;
      return Object.fromEntries(duplicates.flatMap((card, index_0) => isLocked(index_0) ? [] : Object.entries(getDuplicateCardErrors(card, dateRules(new Date()))).map(t8 => {
        const [path, message] = t8;
        return [`${cardPath(index_0)}.${path}`, {
          message
        }];
      })));
    };
    $[7] = dateRules;
    $[8] = isLocked;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const validate = t6;
  const t7 = carried?.card ?? getDuplicateCardDefaults(start);
  let t8;
  if ($[10] !== t7) {
    t8 = {
      duplicates: [t7]
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = t11 => {
      const {
        formApi
      } = t11;
      const invalidPaths = Object.keys(formApi.getAllErrors().fields);
      setCollapsed(current => current.filter(index_2 => !invalidPaths.some(path_0 => path_0.startsWith(`${cardPath(index_2)}.`))));
    };
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== validate) {
    t11 = {
      onChange: t12 => {
        const {
          formApi: formApi_0,
          value: value_0
        } = t12;
        return formApi_0.state.submissionAttempts > 0 || checkingRef.current ? {
          fields: validate(value_0)
        } : undefined;
      }
    };
    $[13] = validate;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const form = useAppForm({
    defaultValues: t8,
    onSubmit: async t9 => {
      const {
        value
      } = t9;
      const next = await save(value.duplicates.map(card_0 => buildDuplicateBody(template, card_0, access)));
      if (!next) {
        return;
      }
      if (!next.every(isSettled)) {
        setCollapsed(next.flatMap(_temp2));
        return;
      }
      const [loadIdCreated] = getCreatedLoadIds(next);
      if (createAnother && loadIdCreated !== undefined) {
        onCreated(loadIdCreated);
        resetOutcome();
        form.reset({
          duplicates: value.duplicates.map(getNextDuplicateCard)
        }, {
          keepDefaultValues: true
        });
        return;
      }
      onSaved(next);
    },
    onSubmitInvalid: t10,
    validators: t11
  });
  const changeCount = next_0 => {
    onCountChange(next_0);
    form.setFieldValue("duplicates", cards => Array.from({
      length: next_0
    }, (_, index_3) => cards[index_3] ?? getDuplicateCardDefaults(start)));
    setCollapsed(current_0 => current_0.filter(index_4 => index_4 < next_0));
  };
  let t12;
  if ($[15] !== form || $[16] !== isLocked) {
    t12 = (next_1, change) => form.setFieldValue("duplicates", current_1 => current_1.map((card_1, index_5) => isLocked(index_5) ? card_1 : change(card_1, next_1)));
    $[15] = form;
    $[16] = isLocked;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const changeStart = t12;
  const originalRate = template.customer_rate;
  const t13 = checked => {
    setKeepDates(checked);
    if (checked) {
      checkingRef.current = true;
    }
    changeStart({
      ...start,
      keepDates: checked
    }, withStartDates);
    form.validate("change");
  };
  let t14;
  if ($[18] !== keepDates || $[19] !== sending || $[20] !== t13) {
    t14 = <CheckboxOption checked={keepDates} disabled={sending} onChange={t13}>Keep dates and times from the original load</CheckboxOption>;
    $[18] = keepDates;
    $[19] = sending;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  const keepDatesOption = t14;
  const rateOption = originalRate ? <CheckboxOption checked={useOriginalRate} disabled={sending} onChange={checked_0 => {
    setUseOriginalRate(checked_0);
    changeStart({
      ...start,
      useOriginalRate: checked_0
    }, withStartRate);
  }}>Use original customer rate ({formatCurrency({
      ...originalRate,
      showCurrencyCode: true,
      twoDecimals: true
    })})</CheckboxOption> : null;
  let t15;
  if ($[22] !== onSaved || $[23] !== skip) {
    t15 = index_6 => {
      const next_2 = skip(index_6);
      if (next_2.every(isSettled)) {
        onSaved(next_2);
      } else {
        setCollapsed(current_2 => [...current_2, index_6]);
      }
    };
    $[22] = onSaved;
    $[23] = skip;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const skipCard = t15;
  let t16;
  if ($[25] !== container || $[26] !== count || $[27] !== dateRules || $[28] !== form || $[29] !== isLocked || $[30] !== keepDatesOption || $[31] !== outcomes || $[32] !== rateOption || $[33] !== sending || $[34] !== skipCard || $[35] !== template.drops || $[36] !== today) {
    t16 = index_7 => {
      const outcome_0 = outcomes[index_7] ?? IDLE;
      const feedback = getFeedback(outcome_0);
      const skipAction = count > 1 && outcome_0.status === "unconfirmed" ? <Button onClick={() => skipCard(index_7)} size="sm" type="button" variant="outline">Skip</Button> : undefined;
      return <DuplicateCard dateRules={dateRules(today)} datesOption={container === "panel" ? keepDatesOption : undefined} error={<ApiErrors action={skipAction} errors={feedback} />} fields={cardPath(index_7)} form={form} layout={container === "dialog" ? "grid" : "column"} pending={sending || isLocked(index_7)} rateOption={container === "panel" ? rateOption : undefined} templateStops={template.drops} data-sentry-element="DuplicateCard" data-sentry-component="renderCard" data-sentry-source-file="duplicate-flow.tsx" />;
    };
    $[25] = container;
    $[26] = count;
    $[27] = dateRules;
    $[28] = form;
    $[29] = isLocked;
    $[30] = keepDatesOption;
    $[31] = outcomes;
    $[32] = rateOption;
    $[33] = sending;
    $[34] = skipCard;
    $[35] = template.drops;
    $[36] = today;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  const renderCard = t16;
  const t17 = count > 1;
  let t18;
  if ($[38] !== form.state.values.duplicates || $[39] !== keepDates || $[40] !== onSwitch || $[41] !== outcomes[0] || $[42] !== useOriginalRate) {
    t18 = () => {
      const [card_2] = form.state.values.duplicates;
      if (card_2) {
        onSwitch({
          card: card_2,
          keepDates,
          outcome: outcomes[0] ?? IDLE,
          useOriginalRate
        });
      }
    };
    $[38] = form.state.values.duplicates;
    $[39] = keepDates;
    $[40] = onSwitch;
    $[41] = outcomes[0];
    $[42] = useOriginalRate;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== container || $[45] !== sending || $[46] !== t17 || $[47] !== t18) {
    t19 = <SwitchContainerButton container={container} disabled={sending} holdsSeveralCards={t17} onSwitch={t18} />;
    $[44] = container;
    $[45] = sending;
    $[46] = t17;
    $[47] = t18;
    $[48] = t19;
  } else {
    t19 = $[48];
  }
  let t20;
  if ($[49] !== collapsed || $[50] !== container || $[51] !== count || $[52] !== form || $[53] !== isLocked || $[54] !== outcomes || $[55] !== renderCard || $[56] !== sending) {
    t20 = container === "panel" ? renderCard(0) : Array.from({
      length: count
    }, (__0, index_8) => <DuplicateSection card={renderCard(index_8)} key={index_8} label={count > 1 ? `Copy ${index_8 + 1}` : "Copy"} onOpenChange={open => setCollapsed(current_3 => open ? current_3.filter(item => item !== index_8) : [...current_3, index_8])} open={!collapsed.includes(index_8)} outcome={outcomes[index_8] ?? IDLE} rate={<DuplicateCardRate fields={{
      customer_rate_amount: `${cardPath(index_8)}.customer_rate_amount`,
      customer_rate_currency: `${cardPath(index_8)}.customer_rate_currency`
    }} form={form} inline={true} pending={sending || isLocked(index_8)} />} summary={<DuplicateCardSummary fields={cardPath(index_8)} form={form} />} />);
    $[49] = collapsed;
    $[50] = container;
    $[51] = count;
    $[52] = form;
    $[53] = isLocked;
    $[54] = outcomes;
    $[55] = renderCard;
    $[56] = sending;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  let t21;
  if ($[58] !== container || $[59] !== createAnother || $[60] !== keepDatesOption || $[61] !== rateOption || $[62] !== sending) {
    t21 = container === "panel" ? <CheckboxOption checked={createAnother} disabled={sending} onChange={setCreateAnother}>Create another duplicate</CheckboxOption> : <div className="flex flex-wrap items-center gap-x-6 gap-y-3"><span className="text-sm leading-none text-muted-foreground">From the original load:</span>{keepDatesOption}{rateOption}</div>;
    $[58] = container;
    $[59] = createAnother;
    $[60] = keepDatesOption;
    $[61] = rateOption;
    $[62] = sending;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== onClose || $[65] !== sending) {
    t22 = <Button disabled={sending} onClick={onClose} size="lg" type="button" variant="secondary">Cancel</Button>;
    $[64] = onClose;
    $[65] = sending;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== changeCount || $[68] !== container || $[69] !== count || $[70] !== sending || $[71] !== settledCount) {
    t23 = container === "dialog" ? <DuplicateCount count={count} disabled={sending} min={settledCount + 1} onChange={changeCount} /> : null;
    $[67] = changeCount;
    $[68] = container;
    $[69] = count;
    $[70] = sending;
    $[71] = settledCount;
    $[72] = t23;
  } else {
    t23 = $[72];
  }
  let t24;
  if ($[73] !== sending) {
    t24 = sending ? <Spinner /> : <CopyPlus />;
    $[73] = sending;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  let t25;
  if ($[75] !== count || $[76] !== settledCount) {
    t25 = getSaveLabel(count, settledCount);
    $[75] = count;
    $[76] = settledCount;
    $[77] = t25;
  } else {
    t25 = $[77];
  }
  let t26;
  if ($[78] !== sending || $[79] !== t24 || $[80] !== t25) {
    t26 = <Button disabled={sending} size="lg" type="submit">{t24}{t25}</Button>;
    $[78] = sending;
    $[79] = t24;
    $[80] = t25;
    $[81] = t26;
  } else {
    t26 = $[81];
  }
  let t27;
  if ($[82] !== t23 || $[83] !== t26) {
    t27 = <div className="flex items-center gap-4">{t23}{t26}</div>;
    $[82] = t23;
    $[83] = t26;
    $[84] = t27;
  } else {
    t27 = $[84];
  }
  let t28;
  if ($[85] !== t22 || $[86] !== t27) {
    t28 = <>{t22}{t27}</>;
    $[85] = t22;
    $[86] = t27;
    $[87] = t28;
  } else {
    t28 = $[87];
  }
  let t29;
  if ($[88] !== opened) {
    t29 = <OriginalLoadNotice origin={opened} />;
    $[88] = opened;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== form) {
    t30 = () => void form.handleSubmit();
    $[90] = form;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  let t31;
  if ($[92] !== container || $[93] !== t19 || $[94] !== t20 || $[95] !== t21 || $[96] !== t28 || $[97] !== t29 || $[98] !== t30) {
    t31 = <DuplicateFrame action={t19} body={t20} container={container} controls={t21} footer={t28} notice={t29} onSubmit={t30} data-sentry-element="DuplicateFrame" data-sentry-component="DuplicateForm" data-sentry-source-file="duplicate-flow.tsx" />;
    $[92] = container;
    $[93] = t19;
    $[94] = t20;
    $[95] = t21;
    $[96] = t28;
    $[97] = t29;
    $[98] = t30;
    $[99] = t31;
  } else {
    t31 = $[99];
  }
  return t31;
};

/**
 * Creates Duplicates of the Original Load `loadId` in the dialog, one or several at once, or in the
 * side panel, one at a time. The flow ends once every Duplicate is created or skipped, unless the
 * side panel stays open for another, or when the user closes it, which is blocked only while a
 * request is in flight. `onClose` receives every Duplicate's outcome, one per card.
 * `onEachCreated` hears of each new Load as soon as it is created. `onSwitch` moves the one
 * Duplicate to the other container. `origin` is what the session read as it opened.
 */
export const DuplicateFlow = t0 => {
  const $ = _c(36);
  const {
    carried,
    container,
    loadId,
    onClose,
    onCreated,
    onEachCreated,
    onSwitch,
    origin
  } = t0;
  const saving = useDuplicateSave(loadId, carried?.outcome, onEachCreated);
  const [count, setCount] = useState(1);
  let t1;
  if ($[0] !== onClose || $[1] !== saving.outcomes || $[2] !== saving.sending) {
    t1 = () => {
      if (!saving.sending) {
        onClose(saving.outcomes);
      }
    };
    $[0] = onClose;
    $[1] = saving.outcomes;
    $[2] = saving.sending;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const close = t1;
  let t2;
  if ($[4] !== close || $[5] !== container) {
    t2 = body => <DuplicateFrame body={body} container={container} footer={<Button onClick={close} size="lg" type="button" variant="secondary">Cancel</Button>} data-sentry-element="DuplicateFrame" data-sentry-component="frame" data-sentry-source-file="duplicate-flow.tsx" />;
    $[4] = close;
    $[5] = container;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const frame = t2;
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ApiErrors errors={"We couldn\u2019t load the details of this load. Try again later."} />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== frame) {
    t4 = frame(t3);
    $[8] = frame;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton className="h-64 w-full" />;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== frame) {
    t6 = frame(t5);
    $[11] = frame;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== saving) {
    t7 = next => {
      setCount(next);
      saving.resize(next);
    };
    $[13] = saving;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== carried || $[16] !== close || $[17] !== container || $[18] !== count || $[19] !== onClose || $[20] !== onCreated || $[21] !== onSwitch || $[22] !== origin || $[23] !== saving || $[24] !== t7) {
    t8 = <DuplicateForm carried={carried} container={container} count={count} onClose={close} onCountChange={t7} onCreated={onCreated} onSaved={onClose} onSwitch={onSwitch} origin={origin} saving={saving} data-sentry-element="DuplicateForm" data-sentry-source-file="duplicate-flow.tsx" />;
    $[15] = carried;
    $[16] = close;
    $[17] = container;
    $[18] = count;
    $[19] = onClose;
    $[20] = onCreated;
    $[21] = onSwitch;
    $[22] = origin;
    $[23] = saving;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t6 || $[27] !== t8) {
    t9 = <Suspense fallback={t6} data-sentry-element="Suspense" data-sentry-source-file="duplicate-flow.tsx">{t8}</Suspense>;
    $[26] = t6;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t4 || $[30] !== t9) {
    t10 = <ErrorBoundary fallback={t4} data-sentry-element="ErrorBoundary" data-sentry-source-file="duplicate-flow.tsx">{t9}</ErrorBoundary>;
    $[29] = t4;
    $[30] = t9;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== close || $[33] !== container || $[34] !== t10) {
    t11 = <DuplicateShell container={container} onClose={close} data-sentry-element="DuplicateShell" data-sentry-component="DuplicateFlow" data-sentry-source-file="duplicate-flow.tsx">{t10}</DuplicateShell>;
    $[32] = close;
    $[33] = container;
    $[34] = t10;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
};
function _temp() {
  return new Date();
}
function _temp2(outcome, index_1) {
  return isSettled(outcome) ? [index_1] : [];
}